import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LoggerService from 'garaje/services/logger';
import type StateService from 'garaje/services/state';
import moment from 'moment-timezone';

const WARNING_THRESHOLD = 75;
const BLOCK_THRESHOLD = 100;

export default class BasicVisitorsGatingService extends Service {
  @service declare featureFlags: FeatureFlagsService;
  @service declare logger: LoggerService;
  @service declare state: StateService;

  @tracked isConnectTenant = false;

  get isFeatureEnabled(): boolean {
    return this.featureFlags.isEnabled('visitors-basic-monthly-cap-emails');
  }

  get isBasicUser(): boolean {
    return !!this.state.vrSubscription?.isBasicUser;
  }

  get hasVisitorLogEntryLimitGatingFeature(): boolean {
    return !!this.state.vrSubscription?.hasVisitorLogEntryLimitGating;
  }

  get isConnectCustomer(): boolean {
    return !!this.state.currentCompany?.connectCustomer;
  }

  get isActive(): boolean {
    return (
      this.isFeatureEnabled &&
      this.isBasicUser &&
      this.hasVisitorLogEntryLimitGatingFeature &&
      !this.isConnectTenant &&
      !this.isConnectCustomer
    );
  }

  get monthlyCount(): number | null {
    return this.state.currentCompany?.visitorLogEntriesThisMonth ?? null;
  }

  get shouldShowWarning(): boolean {
    const count = this.monthlyCount;
    if (!this.isActive || count == null) return false;
    return count >= WARNING_THRESHOLD && count < BLOCK_THRESHOLD;
  }

  get shouldBlock(): boolean {
    const count = this.monthlyCount;
    if (!this.isActive || count == null) return false;
    return count >= BLOCK_THRESHOLD;
  }

  get monthlyCap(): number {
    return BLOCK_THRESHOLD;
  }

  get nextResetLabel(): string {
    return moment.utc().add(1, 'month').startOf('month').format('MMMM');
  }

  loadMonthlyCount = restartableTask(async () => {
    this.isConnectTenant = false;

    if (!this.isFeatureEnabled || !this.isBasicUser) return;

    const location = this.state.currentLocation;
    if (!location) return;

    const isConnect = await location.isConnectedToProperty();
    if (isConnect) {
      this.isConnectTenant = true;
      return;
    }

    try {
      // Network / HTTP rejections surface in the catch below. Task
      // cancellations do not: ember-concurrency transpiles this async task
      // body to a generator and cancels via iterator.return() (not .throw()),
      // which bypasses catch blocks — so no didCancel guard is needed.
      await this.state.currentCompany?.reload();
    } catch (e) {
      // Reload failure is non-fatal: the cached visitorLogEntriesThisMonth
      // persists and self-heals on the next successful task. Spec accepts
      // a 1-min staleness ceiling so a transient blip is acceptable.
      // logger.warn fires on every rejection so sustained outages become visible.
      // TODO: add service-level dedup if Datadog ingestion cost becomes a concern
      //       during broad backend outages (one warning per user per failed call now).
      const companyId = this.state.currentCompany?.id ?? 'unknown';
      this.logger.warn(
        `basic-visitors-gating-reload-failed companyId=${companyId}`,
        e instanceof Error ? e : new Error(String(e)),
      );
    }
  });
}

declare module '@ember/service' {
  interface Registry {
    'basic-visitors-gating': BasicVisitorsGatingService;
  }
}
